import { useState, useEffect } from 'react';
import { getRecurrenceLabel } from '../../utils/recurrenceHelpers';
import { db } from '../../App'; // Import the db instance

export function AbsenceForm({ absentUserId, users, templates, onAbsentUserChange, startDate, onStartDateChange, endDate, onEndDateChange, reassignments, onReassignmentChange }) {

  // We filter the task templates to those assigned to the absent user
  const [absentUserTemplates, setAbsentUserTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  useEffect(() => {
    if (!absentUserId) {
      setAbsentUserTemplates([]);
      return;
    }

    setLoadingTemplates(true);
    // Fetch templates that belong to the active absent user.
    // They represent the recurring tasks that need to be re-allocated.
    const unsubscribe = db.collection('taskTemplates')
      .where('assigneeId', '==', absentUserId)
      .where('isActive', '==', true) // assuming active templates map to active recurring tasks
      .onSnapshot((snapshot) => {
        const data = [];
        snapshot.forEach((doc) => {
          data.push({ id: doc.id, ...doc.data() });
        });
        setAbsentUserTemplates(data);
        setLoadingTemplates(false);
      }, () => setLoadingTemplates(false));

    return () => unsubscribe();
  }, [absentUserId]);


  // Effect to initialize reassignments if the user changes
  useEffect(() => {
    // If the absent user has changed, we might want to clear reassignments or preset them.
    // In this simple version we don't automatically preset, they have to choose.
  }, [absentUserId]);


  // Handler for individual template reassignment
  const handleAssigneeChange = (templateId, newAssigneeId) => {
    onReassignmentChange(prev => ({
      ...prev,
      [templateId]: newAssigneeId
    }));
  };

  // Get users who are not the absent user to act as substitutes
  const substituteUsers = users.filter(u => u.id !== absentUserId && u.active);

  return (
    <div className="absence-form">
      <div className="form-group">
        <label htmlFor="absentUserSelect">Abwesende Person</label>
        <select
          id="absentUserSelect"
          className="form-control"
          value={absentUserId}
          onChange={(e) => onAbsentUserChange(e.target.value)}
          required
        >
          <option value="">Bitte wählen...</option>
          {users.filter(u => u.active).map(user => (
            <option key={user.id} value={user.id}>
              {user.displayName} ({user.role === 'admin' ? 'Admin' : 'Benutzer'})
            </option>
          ))}
        </select>
      </div>

      <div className="date-range-container">
        <div className="form-group">
          <label htmlFor="absenceStartDate">Von (Datum)</label>
          <input
            id="absenceStartDate"
            type="date"
            className="form-control"
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="absenceEndDate">Bis (Datum)</label>
          <input
            id="absenceEndDate"
            type="date"
            className="form-control"
            value={endDate}
            onChange={(e) => onEndDateChange(e.target.value)}
            min={startDate}
            required
          />
        </div>
      </div>

      {absentUserId && (
        <div className="reassignment-list">
          <h4>Betroffene Aufgaben & Vorlagen</h4>
          
          {loadingTemplates ? (
            <div className="loading">Vorlagen werden geladen...</div>
          ) : absentUserTemplates.length === 0 ? (
            <div className="absence-empty-state">
              <i className="fas fa-check-circle"></i>
              <p>Keine aktiven Aufgaben oder Vorlagen vorhanden.</p>
            </div>
          ) : (
            <div className="template-items">
              <p className="help-text" style={{fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '1rem'}}>
                Wähle für jede Vorlage eine Vertretung. Offene Aufgaben dieser Vorlage werden ebenfalls übertragen.
              </p>
              
              {absentUserTemplates.map(template => (
                <div key={template.id} className="reassignment-item">
                  <div className="reassignment-item-header">
                    <h5 className="reassignment-task-title">{template.title}</h5>
                    <div className="reassignment-badges">
                      <span className="badge badge-info">{getRecurrenceLabel(template.recurrence)}</span>
                    </div>
                  </div>
                  <div className="reassignment-controls">
                    <i className="fas fa-arrow-right"></i>
                    <select
                      className="form-control"
                      value={reassignments[template.id] || ''}
                      onChange={(e) => handleAssigneeChange(template.id, e.target.value)}
                      required
                    >
                      <option value="">Vertretung wählen...</option>
                      {substituteUsers.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.displayName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
